// AccountSection — Settings > My account. Exists primarily for App Review
// 5.1.1(v): apps with account creation must offer in-app account deletion.
import React, { useState } from 'react';
import { AlertTriangle, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';
import { isNative, enablePushNotifications, disablePushNotifications } from '../../platform';
import './AccountSection.css';

const PUSH_TOKEN_KEY = 'tekstak.push.token';

export default function AccountSection() {
  const { user, signOut } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [pushOn, setPushOn] = useState(() => {
    try { return Boolean(localStorage.getItem(PUSH_TOKEN_KEY)); } catch (e) { return false; }
  });
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState(null);

  const handleTogglePush = async () => {
    setPushBusy(true);
    setPushError(null);
    try {
      if (!pushOn) {
        const token = await enablePushNotifications();
        if (!token) {
          setPushError('Notifications are blocked. Enable them for TekStak in iOS Settings, then try again.');
          return;
        }
        await apiClient.post('/api/push/tokens', { token, platform: 'ios' });
        localStorage.setItem(PUSH_TOKEN_KEY, token);
        setPushOn(true);
      } else {
        const token = localStorage.getItem(PUSH_TOKEN_KEY);
        if (token) {
          await apiClient.del('/api/push/tokens', { body: { token } });
        }
        await disablePushNotifications();
        localStorage.removeItem(PUSH_TOKEN_KEY);
        setPushOn(false);
      }
    } catch (err) {
      setPushError(err?.message || 'Could not update push notifications. Try again.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    const first = await confirmDialog({
      title: 'Delete account',
      message:
        'This permanently deletes your sign-in and personal data: saved account lists, personal API keys, and your role. ' +
        'Shared workspace records (notes, classifications, tasks) are not affected. This cannot be undone.',
      confirmLabel: 'Continue',
      danger: true,
    });
    if (!first) return;

    const second = await confirmDialog({
      title: 'Permanently delete account?',
      message: `Last check: delete the account for ${user?.email || 'this user'}? You will be signed out immediately and cannot sign back in.`,
      confirmLabel: 'Permanently delete',
      danger: true,
    });
    if (!second) return;

    setDeleting(true);
    setError(null);
    try {
      await apiClient.del('/api/auth/me');
      await signOut();
    } catch (err) {
      setError(err?.message || 'Account deletion failed. Nothing was changed — try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="account-section">
      <h2 className="account-section-title">My account</h2>
      <p className="account-section-email">
        Signed in as <strong>{user?.email || 'unknown'}</strong>
      </p>

      {/* Push toggle only exists in the native iOS shell */}
      {isNative && (
        <div className="account-push">
          <div className="account-push-header">
            <Bell size={18} aria-hidden="true" />
            <span>Push notifications</span>
          </div>
          <p className="account-push-copy">
            Get a daily "where to focus" notification with new deal signals.
          </p>
          {pushError && <p className="account-danger-error" role="alert">{pushError}</p>}
          <button
            type="button"
            className="account-push-button"
            onClick={handleTogglePush}
            disabled={pushBusy}
            aria-pressed={pushOn}
          >
            {pushBusy ? 'Working…' : pushOn ? 'Turn off notifications' : 'Turn on notifications'}
          </button>
        </div>
      )}

      <div className="account-danger-zone">
        <div className="account-danger-header">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>Danger zone</span>
        </div>
        <p className="account-danger-copy">
          Deleting your account removes your sign-in and personal data permanently.
        </p>
        {error && <p className="account-danger-error" role="alert">{error}</p>}
        <button
          type="button"
          className="account-delete-button"
          onClick={handleDeleteAccount}
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete account'}
        </button>
      </div>
    </div>
  );
}
